import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

/** O que o serviço passa ao abrir: a versão em uso e a que o backend passou a exigir. */
export interface AtualizacaoObrigatoriaDialogData {
  versaoEmUso: string;
  versaoMinima: string;
}

/**
 * Aviso de atualização obrigatória, sem saída.
 *
 * <p>É o degrau acima do aviso comum: aqui a versão aberta deixou de conversar com a API, e seguir
 * usando produziria erro a cada ação — ou, pior, gravaria dado pela metade. Por isso não há
 * "depois": nenhum botão de fechar, nenhum `Esc`, nenhum clique fora (`disableClose` de quem
 * abre). A única saída é recarregar.</p>
 *
 * <p>As duas versões aparecem porque é o que o suporte pergunta quando o usuário liga, e é o que
 * o rodapé mostra no dia a dia.</p>
 */
@Component({
  selector: 'fury-atualizacao-obrigatoria-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon class="icone">system_update_alt</mat-icon>
      Atualização necessária
    </h2>
    <mat-dialog-content>
      <p>
        Esta versão do sistema não é mais compatível com o servidor. Recarregue a página para
        continuar; o que estiver preenchido nesta tela será perdido.
      </p>
      <p class="versoes">
        Versão aberta: <strong>{{ data.versaoEmUso }}</strong><br>
        Versão exigida: <strong>{{ data.versaoMinima }}</strong>
      </p>
    </mat-dialog-content>
    <mat-dialog-actions class="acoes">
      <button mat-flat-button color="primary" type="button" (click)="recarregar()">
        RECARREGAR AGORA
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 { display: flex; align-items: center; gap: 8px; }
    .icone { color: var(--m3-primary); }
    .versoes { font-size: 13px; color: var(--m3-on-surface-variant); margin-top: 16px; }
    .acoes { display: flex; justify-content: flex-end; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
})
export class AtualizacaoObrigatoriaDialogComponent {

  readonly data = inject<AtualizacaoObrigatoriaDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject(MatDialogRef<AtualizacaoObrigatoriaDialogComponent, 'recarregar'>);

  /** Quem recarrega é o serviço, que sabe se há build novo em cache para ativar antes. */
  recarregar(): void {
    this.dialogRef.close('recarregar');
  }
}
